import React, { useState, useContext } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSignOutAlt,
  faCog,
  faExchangeAlt,
  faBars,
  faTimes
} from '@fortawesome/free-solid-svg-icons'
import { isEmpty, isUndefined } from 'lodash'
import NavDropdown from 'react-bootstrap/NavDropdown'
import UserContext from 'context/user-context'
import { useAhoy } from 'context/ahoy-context'
import AppSwitcher from 'layout/app-switcher'
import { hasMultipleApps } from 'layout/app-access'
import MunicipalitySwitcher from 'layout/municipality-switcher'
import { canSwitchMunicipality } from 'utils/municipality-access'
import ShieldLogo from 'assets/Shield/Gold/ThirdLineShieldGold.svg'
import { useSidebar } from './sidebar-context'
import SidebarToggleIcon from './sidebar-toggle-icon'

// Above the assistant drawer (1035) and below Bootstrap's modal layer (backdrop 1050), which still
// has to be able to cover this bar. Nothing is occluded: the drawer starts at top: 72 and this bar
// is 54px tall.
const NAVBAR_Z_INDEX = 1036

function TopNavbar({ logo, title, subtitle, navActions }) {
  const { logout } = useAuth0()
  const { clearVisit } = useAhoy()
  const currentUser = useContext(UserContext)
  const { first_name, last_name, municipality_name } = currentUser
  const canSwitch = canSwitchMunicipality(currentUser)
  const [showSwitcher, setShowSwitcher] = useState(false)
  const { isCollapsed, toggleCollapsed, openMobile, closeMobile, mobileOpen, isMobile } = useSidebar()
  const firstName = isUndefined(first_name) ? '' : first_name
  const lastName = isUndefined(last_name) ? '' : last_name
  const fullName = `${firstName} ${lastName}`
  if (fullName.length > 0) {
    localStorage.setItem('fullName', fullName)
  }
  const name = localStorage.getItem('fullName')

  // Bootstrap's .fixed-top sets z-index 1030, which makes this nav a stacking context — so
  // everything inside it, including the report Actions dropdown, painted *behind* the assistant
  // drawer at 1035. Raising the menu on its own could not escape it: a child cannot outrank the
  // stacking context it lives in, so the whole bar has to sit above the drawer.
  return (
    <nav
      className={`navbar navbar-light fixed-top bg-white flex-md-nowrap p-0 shadow-sm app-navbar ${
        isCollapsed ? 'app-navbar-collapsed' : ''
      }`}
      style={{ minHeight: '54px', zIndex: NAVBAR_Z_INDEX }}
    >
      <div
        className="navbar-brand bg-primary app-navbar-brand m-0"
        style={{ minHeight: '54px' }}
      >
        <a
          className="app-navbar-logo d-inline-flex align-items-center"
          href="/dashboards"
          aria-label="ThirdLine home"
        >
          {/* Collapsed desktop: use the shield mark; otherwise the full logo */}
          <img
            src={isCollapsed && !isMobile ? ShieldLogo : logo}
            className="img-fluid"
            alt="ThirdLine Logo"
            style={{ maxHeight: '30px' }}
          />
        </a>

        {/* Desktop: toggle sidebar between full and icons-only. Lives inside
            the brand area so it stays aligned with the sidebar column. */}
        <button
          type="button"
          className="d-none d-md-inline-flex align-items-center justify-content-center sidebar-collapse-toggle"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={isCollapsed}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={toggleCollapsed}
        >
          <SidebarToggleIcon />
        </button>
      </div>

      <div className="container-fluid">
        <div className="d-flex align-items-center">
          {/* Mobile: hamburger (or X when open) toggles the offcanvas sidebar */}
          <button
            type="button"
            className="btn btn-link text-dark d-md-none p-2 me-1 sidebar-mobile-toggle"
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
            onClick={mobileOpen ? closeMobile : openMobile}
          >
            <FontAwesomeIcon icon={mobileOpen ? faTimes : faBars} size="lg" />
          </button>

          <h5 className="text-left mb-0 ms-1 app-navbar-title d-none d-md-block">
            {title}
            {!isEmpty(subtitle) && <small className="ms-2">{subtitle}</small>}
          </h5>
        </div>
        <div className="d-flex align-items-center gap-2">
          {navActions}

          {hasMultipleApps(currentUser) && (
            <AppSwitcher currentApp={'analytics'} />
          )}

          <ul className="navbar-nav px-2">
            <NavDropdown
              id="nav-dropdown-dark-example"
              title={isMobile ? <FontAwesomeIcon icon={faCog} /> : name}
              align="end"
            >
              {canSwitch && (
                <>
                  <NavDropdown.Item disabled className="text-muted small">
                    {municipality_name}
                  </NavDropdown.Item>
                  <NavDropdown.Item onClick={() => setShowSwitcher(true)}>
                    <FontAwesomeIcon icon={faExchangeAlt} /> Change Municipality
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                </>
              )}
              <NavDropdown.Item href="/settings">
                <FontAwesomeIcon icon={faCog} /> Settings
              </NavDropdown.Item>
              <NavDropdown.Item
                onClick={() => {
                  clearVisit()
                  logout({ returnTo: window.location.origin })
                }}
              >
                <FontAwesomeIcon icon={faSignOutAlt} /> Sign out
              </NavDropdown.Item>
            </NavDropdown>
          </ul>
        </div>
      </div>

      {canSwitch && (
        <MunicipalitySwitcher
          show={showSwitcher}
          onHide={() => setShowSwitcher(false)}
        />
      )}
    </nav>
  )
}

export default TopNavbar
